import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { movies, categories } from '@/mocks/movies';
import MovieCard from '@/components/base/MovieCard';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setVisibleCount(12);
    }
  }, [slug]);

  const filteredMovies = useMemo(() => {
    if (activeCategory === 'all') return movies;
    return movies.filter((m) => m.categorySlug === activeCategory);
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryName = currentCategory?.name || '全部电影';

  const loadMore = () => {
    setVisibleCount((prev) => prev + 12);
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 6080影院`,
    description: `6080影院${categoryName}分类，海量${categoryName}免费高清在线观看。`,
    url: `https://www.6080cinema.com/category/${activeCategory}`,
    isPartOf: { '@type': 'WebSite', name: '6080影院' },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <div className="w-full px-4 md:px-6 py-4">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{categoryName}</h1>
            <p className="text-sm text-foreground-500 mt-2">
              共 {filteredMovies.length} 部{categories.find(c => c.slug === activeCategory)?.name || '电影'}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 text-sm rounded-full border transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-accent-500 text-background-50 border-accent-500'
                    : 'bg-background-50 text-foreground-600 border-background-200/60 hover:border-accent-300 hover:text-accent-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredMovies.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {filteredMovies.slice(0, visibleCount).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              {visibleCount < filteredMovies.length && (
                <div className="flex justify-center mt-10">
                  <button
                    onClick={loadMore}
                    className="px-8 py-3 bg-background-100 border border-background-300/60 text-foreground-700 rounded-md hover:bg-background-200 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    加载更多 ({filteredMovies.length - visibleCount} 部)
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-movie-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的电影</p>
              <Link
                to="/"
                className="inline-block mt-4 px-5 py-2 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}